var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import { z } from 'zod';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { accountsType, agreementsNames, documentType, loanProviderRequest, loanResponseStatus, loanStage, marketingAgreementType, providerType, userType, } from './enums';
import { smsCodeInputFieldNames } from '../constants';
export var UuidSchema = z.uuid();
export var taxIdSchema = z.string().length(10);
export var userIdSchema = z.number().nonnegative();
export var userIdInternalSchema = z.string();
export var requestIdSchema = z.string();
export var preLimitIdSchema = z.number().nonnegative();
export var limitIdSchema = z.number().nonnegative();
export var smsIdSchema = z.number().nonnegative();
export var representativeIdSchema = z.number().nonnegative();
export var ibanIdSchema = z.number().nonnegative();
export var spIdSchema = z.string();
export var loanIdSchema = z.number().nonnegative();
export var offerIdSchema = z.string();
export var moneySchema = z.number().nonnegative();
export var currencySchema = z.enum(['PLN', 'OTHER']);
export var emailSchema = z.union([z.email(), z.string()]);
export var periodSchema = z.number().int().min(0).max(30);
export var timestampSchema = z.string();
export var transferId = z.string();
export var ibanSchema = z.string();
export var methodSchema = z.enum(['iframe', 'sms']);
export var createdAtSchema = z.string().refine(function (value) {
    return dayjs(value).isValid();
});
export var updatedAtSchema = z.string().refine(function (value) {
    return dayjs(value).isValid();
});
export var phoneNumberSchema = z.string();
export var peselSchema = z.string();
export var documentSchema = z.object({
    type: documentType.or(z.literal('')),
    number: z.string().or(z.literal('')),
});
export var linkedDocumentSchema = z.object({
    url: z.string().url(),
    type: documentType,
});
var agreementSchema = z.object({
    provider: providerType,
    accepted: z.boolean(),
    timestamp: timestampSchema,
});
var agreementMarketingEmailSchema = agreementSchema.and(z.object({
    email: emailSchema,
    type: z.literal(marketingAgreementType.enum.email),
}));
var agreementMarketingPhoneAndSmsSchema = agreementSchema.and(z.object({
    phoneNumber: phoneNumberSchema,
    type: z.union([
        z.literal(marketingAgreementType.enum.tel),
        z.literal(marketingAgreementType.enum.sms),
    ]),
}));
var agreementMarketingSchema = z.union([
    agreementSchema,
    agreementMarketingEmailSchema,
    agreementMarketingPhoneAndSmsSchema,
]);
export var agreementField = z
    .boolean()
    .optional()
    .refine(function (value) { return value !== false; });
export var agreementsSchema = z.object((_a = {},
    _a[agreementsNames.enum['representative']] = z.array(agreementSchema).nullish(),
    _a[agreementsNames.enum['loanTerms']] = z.array(agreementSchema).nullish(),
    _a[agreementsNames.enum['pragmaTerms']] = z.array(agreementSchema).nullish(),
    _a[agreementsNames.enum['bikbig']] = z.array(agreementSchema).nullish(),
    _a[agreementsNames.enum['professionalContract']] = z.array(agreementSchema).nullish(),
    _a[agreementsNames.enum['marketing']] = z.array(agreementMarketingSchema).nullish(),
    _a[agreementsNames.enum['payProDataProcessing']] = z.array(agreementSchema).nullish(),
    _a[agreementsNames.enum['pragmaGoDataProcessing']] = z.array(agreementSchema).nullish(),
    _a[agreementsNames.enum['bureau']] = z.array(agreementSchema).nullish(),
    _a));
export var paymentTransferSchema = z.object({
    provider: providerType,
    taxId: taxIdSchema,
    pos: z.string(),
    batchId: z.number(),
    details: z.object({
        amount: z.number(),
        currency: currencySchema,
        iban: ibanSchema,
        title: z.string(),
        id: transferId,
        createdAt: createdAtSchema,
    }),
});
export var ImageSchema = z.object({
    src: z.string(),
    alt: z.string(),
});
export var bankAccountSchema = z.object({
    id: ibanIdSchema,
    iban: ibanSchema,
});
export var singleRepresentativeSchema = z.object({
    id: representativeIdSchema,
    name: z.string(),
    surname: z.string(),
    pesel: peselSchema,
    document: documentSchema,
});
export var limitRevenueRate = z.object({
    period: periodSchema,
    pricing: moneySchema,
    loanRepaymentPrice: moneySchema,
    amountMax: moneySchema.nullable(),
    amountMin: moneySchema.nullable(),
    discounted: z
        .object({
        period: z.number(),
        pricing: z.number(),
        loanRepaymentPrice: z.number(),
        amountMax: z.number(),
        amountMin: z.number(),
    })
        .nullish(),
});
export var availableLimitRevenueRate = z.array(limitRevenueRate);
export var offerLimitSchema = z.object({
    id: preLimitIdSchema,
    min: z.number(),
    max: z.number(),
    currency: currencySchema,
    provider: providerType,
    used: z.number().optional(),
    available: z.number(),
    minMaxDifference: z.boolean(),
});
export var preLimitSchema = z.object({
    id: preLimitIdSchema,
    min: z.number(),
    max: z.number(),
    currency: currencySchema,
    provider: providerType,
    availableLimitRevenueRate: availableLimitRevenueRate,
    createdAt: createdAtSchema.nullish(),
    updatedAt: updatedAtSchema.nullish(),
});
export var loanLimitSchema = preLimitSchema.and(z.object({
    available: z.number(),
    minMaxDifference: z.boolean(),
    preLimitId: preLimitIdSchema.optional(),
    used: z.number().optional(),
}));
export var loanLimitMerchantSchema = loanLimitSchema.and(z.object({
    used: z.number(),
}));
export var loanSchema = z.object({
    id: loanIdSchema,
    limitId: limitIdSchema.nullable(),
    amount: moneySchema,
    currency: currencySchema,
    period: periodSchema,
    pricing: moneySchema,
    loanRepaymentPrice: moneySchema,
    name: z.string(),
    iban: ibanSchema,
    provider: providerType,
    stage: loanStage,
    providerRequest: loanProviderRequest,
    sms: z.array(smsIdSchema),
    loanRepaymentAccounts: accountsType,
    previousStage: loanStage.nullable(),
    createdAt: z.string().refine(function (value) {
        return dayjs(value).isValid();
    }),
    documents: z.array(linkedDocumentSchema),
    loanAmountChange: z.boolean(),
    rejectionDescription: z.array(z.string()).nullish(),
    status: loanResponseStatus.optional(),
});
export var logoFieldsSchema = z.object({
    mobile: z.string().url(),
    desktop: z.string().url(),
});
export var logoSchema = z.object({
    pl: logoFieldsSchema,
    en: logoFieldsSchema,
});
export var offerSchema = z.object({
    provider: providerType,
    preLimitId: preLimitIdSchema,
    offerId: offerIdSchema,
    amount: moneySchema,
    currency: currencySchema,
    period: periodSchema,
    pricing: moneySchema,
    loanRepaymentPrice: moneySchema,
    name: z.string(),
    createdAt: createdAtSchema,
    amountMax: moneySchema.nullable(),
    amountMin: moneySchema.nullable(),
});
export var addressSchema = z.object({
    country: z.string(),
    city: z.string(),
    postCode: z.string(),
    street: z.string(),
});
export var personSchema = z.object({
    representative: z.array(z.object({
        id: representativeIdSchema.optional(),
        phone: phoneNumberSchema.optional(),
        email: emailSchema.optional(),
        address: addressSchema.optional(),
    })),
    beneficiary: z.array(z.object({
        id: representativeIdSchema.optional(),
        shares: z.number().optional(),
        phone: phoneNumberSchema.optional(),
        email: emailSchema.optional(),
        address: addressSchema.optional(),
    })),
});
var singleCodeInputSchema = z.string().min(1, {
    error: t('loanProcess.loanSteps.sms.confirmation.form.validation.sms'),
});
export var SmsCodeSchema = z.object({
    smsCode: z.object(__assign({}, smsCodeInputFieldNames.reduce(function (carry, current) {
        carry[current] = singleCodeInputSchema;
        return carry;
    }, {}))),
    finalSmsCode: z.number().optional(),
});
export var beneficiarySchema = z.array(z.object({
    id: representativeIdSchema,
    name: z.string(),
    pesel: peselSchema,
    document: documentSchema,
}));
export var merchantUser = z.object({
    userId: userIdSchema,
    type: userType,
    accessFor: accountsType,
    permission: z.object({
        cancelLoan: z.boolean(),
        continueLoan: z.boolean(),
        newLoan: z.boolean(),
        widget: z.boolean(),
    }),
});
